import "./index.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/auth/AuthContext";
import { ChatProvider } from "@/chat/ChatContext";
import { ProtectedRoute } from "@/auth/ProtectedRoute";
import { AuthLayout } from "@/layouts/AuthLayout";
import { MainLayout } from "@/layouts/MainLayout";
import { LoginPage } from "@/pages/auth/LoginPage";
import { DashboardCliente } from "@/pages/cliente/DashboardCliente";
import { Bollette } from "@/pages/cliente/Bollette";
import { Pratiche } from "@/pages/cliente/Pratiche";
import { DettaglioPratica } from "@/pages/cliente/DettaglioPratica";
import { DettaglioBolletta } from "@/pages/cliente/DettaglioBolletta";
import { Profilo } from "@/pages/cliente/Profilo";
import { DashboardAffiliato } from "@/pages/affiliato/DashboardAffiliato";
import { Commissioni } from "@/pages/affiliato/Commissioni";
import { PraticheAgenzia } from "@/pages/agenzia/PraticheAgenzia";
import { DettaglioPraticaAgenziaPage } from "@/pages/agenzia/DettaglioPraticaAgenzia";
import { ClientiAgenzia } from "@/pages/agenzia/ClientiAgenzia";
import { DettaglioClienteAgenziaPage } from "@/pages/agenzia/DettaglioClienteAgenzia";
import { DashboardAgenzia } from "@/pages/agenzia/DashboardAgenzia";
import { DettaglioBollettaAgenziaPage } from "@/pages/agenzia/DettaglioBollettaAgenzia";
import { ChatPage } from "@/pages/shared/ChatPage";
import { ProfiloStaff } from "@/pages/staff/ProfiloStaff";
import { PreventivoCondominiRotta } from "@/pages/agenzia/PreventivoCondomini";
import { PreventiviCondomini } from "@/pages/agenzia/PreventiviCondomini";
import { QrSelfServiceClienti } from "@/pages/staff/QrSelfServiceClienti";
import { ResetPasswordPage } from "@/pages/auth/ResetPasswordPage";
import { useAuth } from "@/auth/useAuth";
import { paginaArrivoOperatore } from "@/auth/arrivo";
import { Listino } from "@/pages/admin/Listino";
import { Simulatore } from "@/pages/admin/Simulatore";
import { CommissioniCrossAgenzia } from "@/pages/admin/CommissioniCrossAgenzia";
import { Reconcile } from "@/pages/admin/Reconcile";
import { Storni } from "@/pages/admin/Storni";
import { InvioPratiche } from "@/pages/admin/InvioPratiche";
import { Affiliazioni } from "@/pages/admin/Affiliazioni";
import { QrAgenzie } from "@/pages/admin/QrAgenzie";
import { VociMenu } from "@/pages/admin/VociMenu";
import { UtenzeFornitore } from "@/pages/admin/UtenzeFornitore";
import { SupportoPage } from "@/pages/shared/SupportoPage";
import { DashboardRete } from "@/pages/rete/DashboardRete";
import { AgenzieRete } from "@/pages/rete/AgenzieRete";
import { PersoneAgenzia } from "@/pages/rete/PersoneAgenzia";
import { PraticheRete } from "@/pages/rete/PraticheRete";
import { DettaglioPraticaRete } from "@/pages/rete/DettaglioPraticaRete";
import { ClientiRete } from "@/pages/rete/ClientiRete";
import { QuotaRete } from "@/pages/rete/QuotaRete";
import { AffiliazioniRete } from "@/pages/rete/AffiliazioniRete";
import { DettaglioClienteRete } from "@/pages/rete/DettaglioClienteRete";
import { Rete } from "@/pages/admin/Rete";
import { CopiaPreventivo } from "@/pages/pubblico/CopiaPreventivo";
import { QrPercorso } from "@/pages/qr/QrPercorso";

const NewsList = lazy(() => import("@/pages/news/NewsList"));
const NewsDetailPage = lazy(() => import("@/pages/news/NewsDetail"));

function DashboardRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.roles.includes("responsabile")) return <Navigate to="/rete/dashboard" replace />;
  if (user.roles.includes("operatore") && !user.roles.includes("affiliato")) {
    return <Navigate to={paginaArrivoOperatore(user.vociMenu)} replace />;
  }
  if (user.roles.includes("affiliato")) return <Navigate to="/affiliato/dashboard" replace />;
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* QR per agenzia: percorso pubblico del cliente dal telefono, senza login
          né sidebar/header del portale (fuori da AuthLayout e da ProtectedRoute). */}
      <Route path="/q/:token" element={<QrPercorso />} />
      {/* Copia ferma del preventivo per l'amministratore (spec preventivatore § 8): pubblica, sola lettura. */}
      <Route path="/p" element={<CopiaPreventivo />} />

      {/* Public (NewsUrgentiModal is rendered inside MainLayout for authenticated routes) */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/imposta-password" element={<ResetPasswordPage modo="primo-accesso" />} />
      </Route>

      {/* Authenticated */}
      <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
        <Route path="/" element={<DashboardRedirect />} />
        <Route path="/dashboard" element={<DashboardCliente />} />
        <Route path="/bollette" element={<Bollette />} />
        <Route path="/bollette/upload" element={<Bollette />} />
        <Route path="/bollette/:id" element={<DettaglioBolletta />} />
        <Route path="/pratiche" element={<Pratiche />} />
        <Route path="/pratiche/:id" element={<DettaglioPratica />} />
        <Route path="/profilo" element={<Profilo />} />
        <Route path="/chat" element={<ChatPage />} />

        {/* Agenzia (affiliato OR operatore) */}
        <Route path="/agenzia/chat" element={<ProtectedRoute requiredRoles={["affiliato", "operatore"]}><ChatPage /></ProtectedRoute>} />
        <Route path="/agenzia/dashboard" element={<ProtectedRoute requiredRoles={["affiliato", "operatore"]}><DashboardAgenzia /></ProtectedRoute>} />
        <Route path="/agenzia/pratiche" element={<ProtectedRoute requiredRoles={["affiliato", "operatore"]}><PraticheAgenzia /></ProtectedRoute>} />
        <Route path="/agenzia/pratiche/:id" element={<ProtectedRoute requiredRoles={["affiliato", "operatore"]}><DettaglioPraticaAgenziaPage /></ProtectedRoute>} />
        <Route path="/agenzia/clienti" element={<ProtectedRoute requiredRoles={["affiliato", "operatore"]}><ClientiAgenzia /></ProtectedRoute>} />
        <Route path="/agenzia/clienti/:id" element={<ProtectedRoute requiredRoles={["affiliato", "operatore"]}><DettaglioClienteAgenziaPage /></ProtectedRoute>} />
        <Route path="/agenzia/bollette/:id" element={<ProtectedRoute requiredRoles={["affiliato", "operatore"]}><DettaglioBollettaAgenziaPage /></ProtectedRoute>} />
        <Route path="/agenzia/profilo" element={<ProtectedRoute requiredRoles={["affiliato", "operatore"]}><ProfiloStaff /></ProtectedRoute>} />
        <Route path="/preventivo-condomini" element={<ProtectedRoute requiredRoles={["affiliato", "operatore"]}><PreventiviCondomini /></ProtectedRoute>} />
        <Route path="/preventivo-condomini/:id" element={<ProtectedRoute requiredRoles={["affiliato", "operatore"]}><PreventivoCondominiRotta /></ProtectedRoute>} />
        <Route path="/agenzia/qr" element={<ProtectedRoute requiredRole="affiliato"><QrSelfServiceClienti /></ProtectedRoute>} />
        <Route
          path="/supporto"
          element={
            <ProtectedRoute requiredRoles={["affiliato", "operatore"]}>
              <SupportoPage />
            </ProtectedRoute>
          }
        />

        {/* Affiliato only (monitoraggio) */}
        <Route path="/affiliato/dashboard" element={<ProtectedRoute requiredRole="affiliato"><DashboardAffiliato /></ProtectedRoute>} />
        <Route path="/affiliato/commissioni" element={<ProtectedRoute requiredRole="affiliato"><Commissioni /></ProtectedRoute>} />

        {/* Responsabile di rete: sola lettura sulle agenzie in carico */}
        <Route path="/rete/dashboard" element={<ProtectedRoute requiredRole="responsabile"><DashboardRete /></ProtectedRoute>} />
        <Route path="/rete/agenzie" element={<ProtectedRoute requiredRole="responsabile"><AgenzieRete /></ProtectedRoute>} />
        <Route path="/rete/agenzie/:id/persone" element={<ProtectedRoute requiredRole="responsabile"><PersoneAgenzia /></ProtectedRoute>} />
        <Route path="/rete/pratiche" element={<ProtectedRoute requiredRole="responsabile"><PraticheRete /></ProtectedRoute>} />
        <Route path="/rete/pratiche/:id" element={<ProtectedRoute requiredRole="responsabile"><DettaglioPraticaRete /></ProtectedRoute>} />
        <Route path="/rete/clienti" element={<ProtectedRoute requiredRole="responsabile"><ClientiRete /></ProtectedRoute>} />
        <Route path="/rete/clienti/:id" element={<ProtectedRoute requiredRole="responsabile"><DettaglioClienteRete /></ProtectedRoute>} />
        <Route path="/rete/quota" element={<ProtectedRoute requiredRole="responsabile"><QuotaRete /></ProtectedRoute>} />
        <Route path="/rete/affiliazioni" element={<ProtectedRoute requiredRole="responsabile"><AffiliazioniRete /></ProtectedRoute>} />
        <Route path="/rete/profilo" element={<ProtectedRoute requiredRole="responsabile"><ProfiloStaff variante="rete" /></ProtectedRoute>} />

        {/* News / Comunicazioni */}
        <Route
          path="/news"
          element={
            <Suspense fallback={<div className="p-6 text-sm text-muted-foreground">Caricamento…</div>}>
              <NewsList />
            </Suspense>
          }
        />
        <Route
          path="/news/:id"
          element={
            <Suspense fallback={<div className="p-6 text-sm text-muted-foreground">Caricamento…</div>}>
              <NewsDetailPage />
            </Suspense>
          }
        />

        {/* Admin */}
        <Route path="/admin/listino" element={<ProtectedRoute requiredRole="admin"><Listino /></ProtectedRoute>} />
        <Route path="/admin/simulatore" element={<ProtectedRoute requiredRole="admin"><Simulatore /></ProtectedRoute>} />
        <Route path="/admin/commissioni" element={<ProtectedRoute requiredRole="admin"><CommissioniCrossAgenzia /></ProtectedRoute>} />
        <Route path="/admin/reconcile" element={<ProtectedRoute requiredRole="admin"><Reconcile /></ProtectedRoute>} />
        <Route path="/admin/storni" element={<ProtectedRoute requiredRole="admin"><Storni /></ProtectedRoute>} />
        <Route path="/admin/invio-pratiche" element={<ProtectedRoute requiredRole="admin"><InvioPratiche /></ProtectedRoute>} />
        <Route path="/admin/utenze" element={<ProtectedRoute requiredRole="admin"><UtenzeFornitore /></ProtectedRoute>} />
        <Route path="/admin/affiliazioni" element={<ProtectedRoute requiredRole="admin"><Affiliazioni /></ProtectedRoute>} />
        <Route path="/admin/qr-agenzie" element={<ProtectedRoute requiredRole="admin"><QrAgenzie /></ProtectedRoute>} />
        <Route path="/admin/voci-menu" element={<ProtectedRoute requiredRole="admin"><VociMenu /></ProtectedRoute>} />
        <Route path="/admin/rete" element={<ProtectedRoute requiredRole="admin"><Rete /></ProtectedRoute>} />

        {/* Legacy redirects */}
        <Route path="/affiliato/contratti" element={<Navigate to="/agenzia/pratiche" replace />} />
        <Route path="/affiliato/clienti" element={<Navigate to="/agenzia/clienti" replace />} />
        <Route path="/gestione/dashboard" element={<Navigate to="/agenzia/dashboard" replace />} />
        <Route path="/affiliato/sicurezza" element={<Navigate to="/agenzia/profilo" replace />} />
        <Route path="/agenzia/sicurezza" element={<Navigate to="/agenzia/profilo" replace />} />
        <Route path="/admin/invio-a2a" element={<Navigate to="/admin/invio-pratiche" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ChatProvider>
          <AppRoutes />
        </ChatProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
